import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

interface ActivityControllerValue {
  open: boolean;
  openActivity: () => void;
  setActivityOpen: (open: boolean) => void;
}

const ActivityControllerContext = createContext<ActivityControllerValue | null>(
  null,
);

/**
 * Keeps the one piece of shell-owned Activity state independent of the lazy
 * query, SSE, and dialog runtime. Scope mismatches close synchronously, so an
 * open sheet can never flash data from the organization the viewer just left.
 */
export function ActivityControllerProvider({
  scope,
  children,
}: {
  scope: string;
  children: ReactNode;
}) {
  const [state, setState] = useState({ scope, open: false });
  // Changing organization or leaving org-scoped routes is a close, not a
  // suspension. Reset before children commit so returning to an old scope can
  // never resurrect the sheet and move focus without a new user action.
  if (state.scope !== scope) setState({ scope, open: false });
  const open = state.scope === scope && state.open;
  const setActivityOpen = useCallback(
    (nextOpen: boolean) => setState({ scope, open: nextOpen }),
    [scope],
  );
  const openActivity = useCallback(
    () => setState({ scope, open: true }),
    [scope],
  );
  const value = useMemo(
    () => ({ open, openActivity, setActivityOpen }),
    [open, openActivity, setActivityOpen],
  );

  return (
    <ActivityControllerContext.Provider value={value}>
      {children}
    </ActivityControllerContext.Provider>
  );
}

export function useActivityController(): ActivityControllerValue {
  const controller = useContext(ActivityControllerContext);
  if (!controller) {
    throw new Error(
      "useActivityController must be used inside ActivityControllerProvider",
    );
  }
  return controller;
}
